import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import userAPI from '../services/userAPI';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './ProfilePage.css';

const emptyProfile = {
  full_name: '',
  email: '',
  phone_number: ''
};

const emptyPreferences = {
  preferred_room_type: '',
  dietary_requirements: '',
  notifications_enabled: true,
  concierge_assistance: false
};

const ProfilePage = () => {
  const { userProfile } = useAuth();
  const [profile, setProfile] = useState(emptyProfile);
  const [preferences, setPreferences] = useState(emptyPreferences);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [hotelInfo, setHotelInfo] = useState(null);
  const [hotelLoading, setHotelLoading] = useState(true);
  const [stats, setStats] = useState({
    bookings: 0,
    upcomingBookings: 0,
    totalReviews: 0
  });
  const [bookings, setBookings] = useState([]);

  const preferenceOptions = useMemo(() => ({
    roomTypes: [
      { value: '', label: 'No preference' },
      { value: 'standard', label: 'Standard Room' },
      { value: 'deluxe', label: 'Deluxe Room' },
      { value: 'suite', label: 'Suite' },
      { value: 'penthouse', label: 'Penthouse' }
    ]
  }), []);

  const mergePreferences = (rawPreferences) => ({
    ...emptyPreferences,
    ...(rawPreferences || {})
  });

  const loadProfile = async () => {
    setProfileLoading(true);
    try {
      const response = await userAPI.getCurrentUser();
      if (response?.success && response?.data) {
        setProfile({
          full_name: response.data.full_name || '',
          email: response.data.email || '',
          phone_number: response.data.phone_number || '',
          role: response.data.role || ''
        });
        if (response.data.preferences) {
          setPreferences(mergePreferences(response.data.preferences));
        }
      }
    } catch (error) {
      toast.error(error.message || 'Unable to load your profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const loadHotelInfo = async () => {
    // Only load hotel info if user is a manager
    if (userProfile?.role !== 'manager') {
      setHotelLoading(false);
      return;
    }

    setHotelLoading(true);
    try {
      const response = await api.get('/iot-manager/hotel-info');
      if (response?.data?.success && response?.data?.data) {
        setHotelInfo(response.data.data);
      }
    } catch (error) {
      console.warn('Failed to load hotel information:', error);
    } finally {
      setHotelLoading(false);
    }
  };

  const loadPreferences = async () => {
    try {
      const response = await userAPI.getCurrentPreferences();
      if (response?.success) {
        setPreferences(mergePreferences(response.data));
      }
    } catch (error) {
      // Preferences are optional; log to console for debugging
      console.warn('Failed to load preferences', error);
    }
  };

  const loadBookings = async () => {
    try {
      const response = await userAPI.getCurrentBookings();
      if (response?.success && Array.isArray(response.data)) {
        setBookings(response.data);
        const upcoming = response.data.filter((booking) => {
          const startDate = booking?.check_in_date || booking?.start_date;
          return startDate ? new Date(startDate) >= new Date() : false;
        });
        setStats((prev) => ({
          ...prev,
          bookings: response.data.length,
          upcomingBookings: upcoming.length
        }));
      }
    } catch (error) {
      console.warn('Failed to load bookings', error);
    }
  };

  const loadReviews = async () => {
    try {
      const response = await userAPI.getCurrentReviews();
      if (response?.success && Array.isArray(response.data)) {
        setStats((prev) => ({
          ...prev,
          totalReviews: response.data.length
        }));
      }
    } catch (error) {
      console.warn('Failed to load reviews', error);
    }
  };

  useEffect(() => {
    loadProfile();
    loadPreferences();
    loadBookings();
    loadReviews();
    loadHotelInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.role]);

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePreferenceChange = (event) => {
    const { name, value, type, checked } = event.target;
    setPreferences((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setProfileSaving(true);
    try {
      const payload = {
        full_name: profile.full_name,
        email: profile.email,
        phone_number: profile.phone_number
      };
      const response = await userAPI.updateProfile(payload);
      if (response?.success) {
        toast.success('Profile updated successfully');
        await loadProfile();
      } else {
        throw new Error(response?.message || 'Failed to update profile');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePreferencesSubmit = async (event) => {
    event.preventDefault();
    setPreferencesSaving(true);
    try {
      const response = await userAPI.updateCurrentPreferences(preferences);
      if (response?.success) {
        toast.success('Preferences updated');
      } else {
        throw new Error(response?.message || 'Failed to update preferences');
      }
    } catch (error) {
      toast.error(error.message || 'Could not save preferences');
    } finally {
      setPreferencesSaving(false);
    }
  };

  return (
    <div className="profile-page">
      <header className="profile-header">
        <h1>Your Profile</h1>
        <p>Manage your personal information, stay preferences, and recent activity.</p>
      </header>

      {/* Manager Hotel Information Banner */}
      {userProfile?.role === 'manager' && hotelInfo && (
        <div className="manager-hotel-banner">
          <div className="hotel-banner-content">
            <div className="hotel-badge">Hotel Manager</div>
            <h2 className="hotel-name">{hotelInfo.name}</h2>
            <div className="hotel-details">
              <span className="hotel-location">
                📍 {hotelInfo.address}, {hotelInfo.city}, {hotelInfo.country}
              </span>
            </div>
            {hotelInfo.smartthings_location_id && (
              <span className="smartthings-badge">🔗 SmartThings Connected</span>
            )}
          </div>
        </div>
      )}

      {userProfile?.role === 'manager' && hotelLoading && (
        <div className="manager-hotel-banner loading">
          <p>Loading hotel information...</p>
        </div>
      )}

      <section className="profile-stats">
        <div className="stat-card">
          <span className="stat-value">{stats.bookings}</span>
          <span className="stat-label">Total bookings</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.upcomingBookings}</span>
          <span className="stat-label">Upcoming stays</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.totalReviews}</span>
          <span className="stat-label">Reviews submitted</span>
        </div>
      </section>

      <div className="profile-content">
        <section className="profile-card">
          <h2>Personal information</h2>
          {profileLoading ? (
            <p className="profile-placeholder">Loading profile details...</p>
          ) : (
            <form className="profile-form" onSubmit={handleProfileSubmit}>
              <div className="form-row">
                <label htmlFor="full_name">Full name</label>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  value={profile.full_name}
                  onChange={handleProfileChange}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="form-row">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={profile.email}
                  onChange={handleProfileChange}
                  placeholder="name@example.com"
                  required
                />
              </div>

              <div className="form-row">
                <label htmlFor="phone_number">Phone number</label>
                <input
                  id="phone_number"
                  name="phone_number"
                  type="tel"
                  value={profile.phone_number}
                  onChange={handleProfileChange}
                  placeholder="+1 234 567 890"
                />
              </div>

              <div className="form-actions">
                <button type="submit" disabled={profileSaving}>
                  {profileSaving ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </form>
          )}
        </section>

        <section className="profile-card">
          <h2>Stay preferences</h2>
          <form className="profile-form" onSubmit={handlePreferencesSubmit}>
            <div className="form-row">
              <label htmlFor="preferred_room_type">Preferred room type</label>
              <select
                id="preferred_room_type"
                name="preferred_room_type"
                value={preferences.preferred_room_type}
                onChange={handlePreferenceChange}
              >
                {preferenceOptions.roomTypes.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label htmlFor="dietary_requirements">Dietary requirements</label>
              <input
                id="dietary_requirements"
                name="dietary_requirements"
                type="text"
                value={preferences.dietary_requirements}
                onChange={handlePreferenceChange}
                placeholder="Allergies, preferences, etc."
              />
            </div>

            <div className="form-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="notifications_enabled"
                  checked={preferences.notifications_enabled}
                  onChange={handlePreferenceChange}
                />
                Receive booking updates and offers
              </label>
            </div>

            <div className="form-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="concierge_assistance"
                  checked={preferences.concierge_assistance}
                  onChange={handlePreferenceChange}
                />
                Proactive concierge assistance
              </label>
            </div>

            <div className="form-actions">
              <button type="submit" disabled={preferencesSaving}>
                {preferencesSaving ? 'Saving...' : 'Save preferences'}
              </button>
            </div>
          </form>
        </section>

        <section className="profile-card">
          <h2>Recent bookings</h2>
          {bookings.length === 0 ? (
            <p className="profile-placeholder">No bookings yet. Ready for your next stay?</p>
          ) : (
            <ul className="booking-list">
              {bookings.slice(0, 3).map((booking) => {
                const startDate = booking?.check_in_date || booking?.start_date;
                const endDate = booking?.check_out_date || booking?.end_date;
                const bookingKey = booking.booking_id || booking.bookingId || booking.id || `${startDate}-${endDate}`;
                return (
                  <li key={bookingKey} className="booking-item">
                    <div>
                      <h3>{booking.property_name || 'InnBotics Stay'}</h3>
                      <p className="booking-dates">
                        {startDate ? new Date(startDate).toLocaleDateString() : '—'}
                        {' '}–{' '}
                        {endDate ? new Date(endDate).toLocaleDateString() : '—'}
                      </p>
                    </div>
                    <span className={`booking-status booking-status-${(booking.status || 'pending').toLowerCase()}`}>
                      {booking.status || 'Pending'}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default ProfilePage;
